import { createRouter as createTanStackRouter } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import { NotFound } from './components/ui';
import { useCaptureRouteError } from './components/ui/error-boundary';

const DefaultRouteErrorComponent = ({ error, reset }: { error: Error; reset?: () => void }) => {
  useCaptureRouteError(error);

  return (
    <div className='min-h-screen flex items-center justify-center bg-background text-foreground'>
      <div className='max-w-md p-6 bg-card rounded-lg shadow-lg text-center border border-border'>
        <h2 className='text-xl font-bold text-destructive mb-4'>🚨 Route Error</h2>
        <p className='text-muted-foreground mb-4'>Something went wrong loading this page.</p>

        {import.meta.env.DEV && (
          <details className='text-left text-xs bg-muted text-foreground p-3 rounded mb-4'>
            <summary className='cursor-pointer font-medium'>Error Details</summary>
            <pre className='mt-2 text-destructive overflow-auto'>{error?.message || 'Unknown error'}</pre>
            {error?.stack && <pre className='mt-2 text-muted-foreground overflow-auto text-xs'>{error.stack}</pre>}
          </details>
        )}

        <div className='flex flex-col sm:flex-row gap-3'>
          {reset && (
            <button onClick={reset} className='inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium shadow-sm hover:bg-accent hover:text-accent-foreground'>
              Try Again
            </button>
          )}
          <button onClick={() => window.location.reload()} className='inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90'>
            Reload Page
          </button>
        </div>
      </div>
    </div>
  );
};

export const createRouter = () => {
  return createTanStackRouter({
    routeTree,
    // Optimized preload settings for better performance
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 30000, // 30 seconds cache
    defaultStaleTime: 30000,
    defaultPreloadDelay: 50, // 50ms delay before preload

    // Optimized pending component (shadcn/ui styling)
    defaultPendingComponent: () => (
      <div className='min-h-screen flex items-center justify-center bg-background text-foreground'>
        <div className='animate-spin h-10 w-10 rounded-full border-4 border-primary/30 border-t-primary' />
      </div>
    ),

    // Enhanced error component using shadcn/ui tokens
    defaultErrorComponent: DefaultRouteErrorComponent,

    // Enhanced not found component with navigation
    defaultNotFoundComponent: () => <NotFound title='Page Not Found' message="The page you're looking for doesn't exist or has been moved." />,

    // Performance optimizations
    defaultGcTime: 30000, // 30 seconds garbage collection
    notFoundMode: 'root',
  });
};

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createRouter>;
  }
}
